import getCompanyApi from '@/api/get/company/get-company';
import getCurrenciesApi from '@/api/get/currency/get-currencies';
import updateCompanyCurrencyApi from '@/api/patch/currency/update-company-currency';
import updateUserCurrencyApi from '@/api/patch/currency/update-user-currency';
import CurrencySettingsModal from '@/components/modal/modal-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import { Subtitle2Text } from '@/utils/global-fonts';
import { useContext, useEffect, useRef, useState } from 'react';
import Alert from '../alert/alert-component';
import Card from '../card/card-component';
import FormWrapper from '../form-wrapper/form-wrapper-component';
import Loader from '../loader/loader-component';
import Tooltip from '../tooltip/tooltip-component';
import Styled from './currency-component-style';
import CurrencySettingsForm from './currency-settings-form';

/**
 * Currency component.
 * @returns {JSX.Element} The rendered Currency component.
 */
const Currency = () => {
  const [dropdown, setDropdown] = useState(false);
  const [showCurrencySettingsModal, setShowCurrencySettingsModal] =
    useState(false);
  const popupContainerRef = useRef(null);
  const { currentUser, selectedCurrency, setSelectedCurrency } =
    useContext(APP_CONTEXT);
  const currentUserId = currentUser?.id;

  const disableCurrencySettings = !currentUser?.company_id;

  const toggleDropdown = () => {
    setDropdown((prevDropdown) => !prevDropdown);
  };

  const openCurrencySettingsModal = () => {
    setShowCurrencySettingsModal(true);
  };

  const closeCurrencySettingsModal = () => {
    setShowCurrencySettingsModal(false);
  };

  // Queries
  const { data: currencies, loading: currenciesLoading } =
    useQuery(getCurrenciesApi);

  // Mutations
  const [updateUserCurrency, { loading: updateUserCurrencyLoading }] =
    useMutation(updateUserCurrencyApi);

  const [updateCompanyCurrency, { loading: updateCompanyCurrencyLoading }] =
    useMutation(updateCompanyCurrencyApi);

  const loading =
    updateUserCurrencyLoading ||
    currenciesLoading ||
    updateCompanyCurrencyLoading;

  const onSubmitForm = (formData) => {
    updateCompanyCurrency(currentUser.company_id, formData)
      .then(() => {
        closeCurrencySettingsModal();
        Alert({
          icon: 'success',
          text: 'Company currency updated successfully',
        });
      })
      .catch(() => {
        Alert({
          icon: 'error',
          text: 'There was a problem updating your Company currency',
        });
      });
  };

  const selectNewUserCurrency = (currency) => {
    if (currency === selectedCurrency) {
      return;
    }

    updateUserCurrency({
      id: currentUserId,
      currency_id: currency.id,
    })
      .then(() => {
        setSelectedCurrency(currency);
      })
      .catch(() => {
        Alert({
          icon: 'error',
          text: 'There was a problem updating your currency',
        });
      });
  };

  const userName = currentUser?.first_name || 'this user';
  const tooltipText = disableCurrencySettings
    ? `Please assign a company to ${userName} to change the currency.`
    : '';

  useEffect(() => {
    if (currentUser?.currency_id) {
      setSelectedCurrency(
        currencies?.find(
          (currency) => currency.id === currentUser?.currency_id,
        ),
      );
    }
  }, [currentUser, currencies, setSelectedCurrency]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target)
      ) {
        setDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const cardBodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderTop: 'none',
  };

  return (
    <Styled.Main onClick={toggleDropdown} ref={popupContainerRef}>
      {!loading ? (
        <Styled.CurrencyText>
          {selectedCurrency?.name ?? 'Please Select'}
        </Styled.CurrencyText>
      ) : (
        <Loader loading size={25} />
      )}
      <Styled.DropdownIcon size={24} $active={dropdown} />
      {dropdown && (
        <Styled.DropDown>
          <Card
            className="card"
            width="200px"
            margin="6px 0px 0px 0px"
            borderRadius="4px 4px 4px 4px"
            padding="0px 0px 0px 0px"
            bodyStyle={cardBodyStyle}
          >
            <Styled.DropDownHeading>
              <Styled.Title>CURRENCY</Styled.Title>
            </Styled.DropDownHeading>
            <Tooltip
              id="Tooltip"
              text={tooltipText}
              icon={
                <Styled.Option
                  $disabled={disableCurrencySettings}
                  onClick={
                    disableCurrencySettings ? null : openCurrencySettingsModal
                  }
                >
                  <Styled.GearIcon size={16} />
                  <Subtitle2Text>Currency Settings</Subtitle2Text>
                </Styled.Option>
              }
            />
            {currencies?.map((currency) => (
              <Styled.Option
                key={currency.id}
                $selected={selectedCurrency === currency}
                onClick={() => selectNewUserCurrency(currency)}
              >
                <Styled.CurrencySymbol>{currency.symbol}</Styled.CurrencySymbol>
                <Subtitle2Text>{currency.name}</Subtitle2Text>
              </Styled.Option>
            ))}
          </Card>
        </Styled.DropDown>
      )}
      <CurrencySettingsModal
        open={showCurrencySettingsModal}
        close={closeCurrencySettingsModal}
        width="30%"
        alignItems="flex-start"
        title="Currency Settings"
        titleFontSize="28px"
        bodyStyle={{ width: '100%', padding: 0 }}
        minHeight="500px"
      >
        <FormWrapper
          onSubmit={onSubmitForm}
          defaultValues={async () => {
            const currentUserCompany = await getCompanyApi(
              currentUser?.company_id,
            );
            return currentUserCompany?.data?.company;
          }}
        >
          <CurrencySettingsForm
            currencies={currencies}
            closeCurrencySettingsModal={() => closeCurrencySettingsModal()}
            isLoading={loading}
          />
        </FormWrapper>
      </CurrencySettingsModal>
    </Styled.Main>
  );
};

export default Currency;
